/**
 * 책상 반파/뒤집기 연출. desk:destroyed를 받으면:
 *  1) 온전한 상판 메쉬/데스크매트를 숨기고 정적 책상 콜라이더를 제거한다.
 *  2) 기본(내리치기 반파): 상판 반쪽 슬랩 2개를 동적 바디로 스폰 — 가운데가 꺼지며
 *     V자(\/)로 꺾여 내려앉고, 쪼개진 자리에서 나무 스플린터를 버스트(DebrisPool 재사용).
 *     flip(레이지 무브 책상 뒤집기): 상판 전체를 슬랩 1개로 스폰해 앞모서리가 들리며
 *     뒤로 통째로 뒤집혀 날아간다(쪼개지지 않으므로 스플린터 없음).
 * 세션 시작/리셋 때 슬랩을 치우고 상판·콜라이더를 복원한다.
 */
import * as THREE from 'three';
import type RAPIER from '@dimforge/rapier3d-compat';
import type { EventBus } from '../core/EventBus';
import type { BreakableObjectDef, Vec3 } from '../core/types';
import type { PhysicsWorld } from '../physics/PhysicsWorld';
import type { DebrisPool } from './DebrisPool';
import { LAYOUT } from '../config/layout';

/** 나무 스플린터 버스트용 합성 def(카탈로그 밖 — 파괴 대상 아님, 파편 팔레트는 월넛 단색 폴백). */
const DESK_SPLINTER_DEF: BreakableObjectDef = {
  id: 'desk',
  label: '책상',
  breakThreshold: 99,
  fragmentCount: 12,
  baseScore: 0,
  mass: 8,
  color: 0x5a4433,
  primitive: { kind: 'box', size: [0.9, 0.06, 0.5] },
  sound: { layers: ['plastic'] }, // 미사용(책상 파괴음은 AudioBus가 desk:destroyed로 직접 재생)
};

interface Half {
  body: RAPIER.RigidBody;
  mesh: THREE.Mesh;
}

export class DeskBreak {
  private halves: Half[] = [];
  private destroyed = false;
  /** 현재 살아있는 정적 책상 콜라이더 body(파괴 시 제거, 복원 시 재생성). */
  private deskBody: RAPIER.RigidBody;

  constructor(
    private physics: PhysicsWorld,
    private scene: THREE.Scene,
    bus: EventBus,
    private deskMesh: THREE.Mesh,
    private deskPad: THREE.Mesh,
    initialDeskBody: RAPIER.RigidBody,
    /** 복원용 책상 콜라이더 팩토리(main.ts의 치수 상수를 소유한 쪽에서 주입). */
    private makeDeskCollider: () => RAPIER.RigidBody,
    private debris: DebrisPool,
    private now: () => number,
  ) {
    this.deskBody = initialDeskBody;
    bus.on('desk:destroyed', (e) => (e.flip ? this.flip(e.impactSpeed) : this.break(e.position, e.impactSpeed)));
    const restore = (): void => this.restore();
    bus.on('session:start', restore);
    bus.on('session:reset', restore);
  }

  /** 검증용: 파괴 상태 여부. */
  get isDestroyed(): boolean {
    return this.destroyed;
  }

  /** 상판/매트 숨김 + 정적 콜라이더 제거(잔여 파편·물건이 바닥까지 떨어지도록). 공통 진입. */
  private takeDown(): boolean {
    if (this.destroyed) return false;
    this.destroyed = true;
    this.deskMesh.visible = false;
    this.deskPad.visible = false;
    this.physics.removeBody(this.deskBody);
    return true;
  }

  /** 레이지 무브 책상 뒤집기: 상판 전체가 앞모서리부터 들리며 뒤로 통째로 넘어간다. */
  private flip(impactSpeed: number): void {
    if (!this.takeDown()) return;
    const [w, h, d] = LAYOUT.desk.size;
    const topY = LAYOUT.desk.topY;
    const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), this.deskMesh.material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.scene.add(mesh);

    // 슬랩은 1~2개뿐이라 CCD 비용이 미미하고, 얇은(6cm) 판이 고속 회전하며 물건을 쳐내는
    // 연출이라 CCD를 유지한다(끄면 발사된 물건과의 상호작용이 드물게 달라져 잔존 가능).
    const body = this.physics.addDynamicBody({ x: 0, y: topY - h / 2, z: 0 });
    this.physics.attachCuboid(body, { x: w / 2, y: h / 2, z: d / 2 }, 24, { kind: 'debris' }, false);
    // -x 각속도가 앞(+z) 모서리를 들어올린다(ω × r) → 카메라에서 멀어지는 클래식 테이블 플립.
    // 역동성: 공중에서 4~5바퀴 도는 맹렬한 회전 + y/z 무작위 스핀(비대칭 텀블) + 높고 뒤로
    // 크게 던지기(천장을 스치면 부딪혀 튕기는 것까지 연출). impactSpeed = rage.flip.up.
    const omega = 16 + Math.min(10, impactSpeed * 0.7);
    body.setAngvel(
      { x: -omega, y: (Math.random() - 0.5) * 5, z: (Math.random() - 0.5) * 2.5 },
      true,
    );
    body.setLinvel(
      { x: (Math.random() - 0.5) * 1.2, y: 0.7 * impactSpeed, z: -0.42 * impactSpeed },
      true,
    );
    this.halves.push({ body, mesh });

    // 프레임에서 뜯겨나가는 나무 스플린터 — 앞모서리 양쪽에서 버스트.
    const up = new THREE.Vector3(0.1, 1, 0.5);
    const q = new THREE.Quaternion();
    this.debris.spawnBurst(DESK_SPLINTER_DEF, new THREE.Vector3(-w / 4, topY, d / 2), q, up, this.now(), undefined, impactSpeed);
    this.debris.spawnBurst(DESK_SPLINTER_DEF, new THREE.Vector3(w / 4, topY, d / 2), q, up, this.now(), undefined, impactSpeed * 0.9);
  }

  private break(at: Vec3, impactSpeed: number): void {
    if (!this.takeDown()) return;

    // 반쪽 슬랩 2개 — 가운데(쪼개진 선)가 꺼지며 V자로 꺾여 내려앉음.
    const [w, h, d] = LAYOUT.desk.size;
    const topY = LAYOUT.desk.topY;
    const mat = this.deskMesh.material;
    for (const side of [-1, 1] as const) {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(w / 2 - 0.015, h, d), mat);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.scene.add(mesh);

      const body = this.physics.addDynamicBody({ x: (side * w) / 4, y: topY - h / 2, z: 0 }); // 슬랩은 CCD 유지(flip과 동일 이유)
      // 바닥 착지 클래터는 debris↔static 경로로 이미 소리가 난다(이벤트는 정적 쪽에서 발생).
      this.physics.attachCuboid(
        body,
        { x: w / 4 - 0.0075, y: h / 2, z: d / 2 },
        16,
        { kind: 'debris' },
        false,
      );
      // V자 붕괴: 바깥 모서리를 축으로 안쪽(쪼개진) 모서리가 아래로 꺾인다.
      // 정면(+z) 기준 +z 각속도는 반시계 — 왼쪽(side=-1)은 -ω(시계)로 안쪽(+x) 모서리가,
      // 오른쪽(side=+1)은 +ω(반시계)로 안쪽(-x) 모서리가 내려가 함께 \/를 만든다.
      // 바깥 축 회전을 각속도 + 중심 속도(v = ω × r, r = w/4)로 근사: 중심은 ω·w/4로 하강.
      // 안쪽으로 살짝 밀어 바닥에서 쪼개진 모서리가 가운데에 모이게 한다.
      const omega = 1.8 + Math.min(3, impactSpeed * 0.06);
      body.setAngvel({ x: 0, y: 0, z: side * omega }, true);
      body.setLinvel({ x: -side * 0.3, y: -omega * (w / 4), z: 0 }, true);
      this.halves.push({ body, mesh });
    }

    // 쪼개진 자리 나무 스플린터 버스트 — 임팩트 지점 + 중앙 분할선에서 두 번.
    const up = new THREE.Vector3(0.15, 1, 0.1);
    const q = new THREE.Quaternion();
    this.debris.spawnBurst(DESK_SPLINTER_DEF, new THREE.Vector3(at.x, topY, at.z), q, up, this.now(), undefined, impactSpeed);
    this.debris.spawnBurst(DESK_SPLINTER_DEF, new THREE.Vector3(0, topY, 0), q, up, this.now(), undefined, impactSpeed * 0.8);
  }

  /** 매 프레임: 반쪽 슬랩 메쉬를 물리 바디에 동기화. */
  sync(): void {
    for (const { body, mesh } of this.halves) {
      const t = body.translation();
      const r = body.rotation();
      mesh.position.set(t.x, t.y, t.z);
      mesh.quaternion.set(r.x, r.y, r.z, r.w);
    }
  }

  /** 세션 시작/리셋: 반쪽 제거 + 상판/콜라이더 복원. */
  private restore(): void {
    if (!this.destroyed) return;
    for (const { body, mesh } of this.halves) {
      this.physics.removeBody(body);
      this.scene.remove(mesh);
      mesh.geometry.dispose(); // 머티리얼은 상판과 공유 — dispose 금지
    }
    this.halves = [];
    this.deskMesh.visible = true;
    this.deskPad.visible = true;
    this.deskBody = this.makeDeskCollider();
    this.destroyed = false;
  }
}
